// frontend/src/components/Chat/Chat.js
// Purpose: A multimodal chat interface for sending text and images to ChatGPT.
// Desired outcome: Allows users to interact with OpenAI using both text and image inputs with persistent conversation context.

import React, { useState, useRef, useEffect } from 'react';
import api from '../../utils/api';
import './Chat.css';

const Chat = () => {
    const [messages, setMessages] = useState([]);  // Stores chat history
    const [input, setInput] = useState('');  // Stores user input
    const [image, setImage] = useState(null);  // Stores selected image
    const [loading, setLoading] = useState(false);  // API loading state
    const [previewUrl, setPreviewUrl] = useState(null); // Stores preview URL for selected image
    const [conversationId, setConversationId] = useState(null); // Tracks conversation session
    const fileInputRef = useRef(null); // Ref for the file input
    const messagesEndRef = useRef(null); // Ref for auto-scrolling

    // Auto-scroll to bottom whenever messages change
    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages]);

    // Function to generate a unique conversation ID
    useEffect(() => {
        if (!conversationId) {
            setConversationId(`chat-${Date.now()}-${Math.floor(Math.random() * 1000)}`);
        }
    }, [conversationId]);

    // Function to handle file selection
    const handleImageChange = (event) => {
        const file = event.target.files[0];
        if (file && file.type.startsWith('image/')) {
            setImage(file);
            // Create a preview URL for the selected image
            const objectUrl = URL.createObjectURL(file);
            setPreviewUrl(objectUrl);
            console.log("✅ Image selected:", file.name, file.type, `${Math.round(file.size / 1024)} KB`);
        } else {
            alert('Please select a valid image file.');
            setImage(null);
            setPreviewUrl(null);
            event.target.value = null; // Clear the invalid selection
        }
    };

    // Function to send message & image
    const sendMessage = async () => {
        if (!input.trim() && !image) return;

        setLoading(true);
        const formData = new FormData();
        
        // Always append message, even if empty
        formData.append('message', input.trim());
        
        // Always send the conversation ID to maintain context
        if (conversationId) {
            formData.append('conversationId', conversationId);
        }
        
        // Only append image on first message or when a new image is selected
        if (image) {
            formData.append('image', image);
            console.log("✅ Image appended to FormData:", image.name, image.type, `${Math.round(image.size / 1024)} KB`);
        }
        
        // Add previous messages for context (simplified version)
        if (messages.length > 0) {
            formData.append('hasContextHistory', 'true');
        }

        console.log("📡 Sending request to API with FormData:");
        for (let [key, value] of formData.entries()) {
            console.log(`   ${key}:`, typeof value === 'object' && value !== null ? 
                `${value.name || 'object'} (${value.type || 'unknown type'})` : value);
        }

        try {
            // Create temporary user message with image preview
            const tempUserMessage = { 
                role: 'user', 
                content: input, 
                image: previewUrl // Use the preview URL for display in UI
            };
            setMessages(prevMessages => [...prevMessages, tempUserMessage]);
            
            // Make API request
            const response = await api.post('/api/chat', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data' // Ensure correct content type
                }
            });

            console.log("🎯 API Response:", response); // Log full response

            if (response.success && response.data && response.data.reply) {
                setMessages(prevMessages => [
                    ...prevMessages, 
                    { role: 'assistant', content: response.data.reply }
                ]);
            } else {
                throw new Error("Invalid response structure");
            }
        } catch (error) {
            console.error('❌ Chat request failed:', error);
            
            // Add error message to chat
            setMessages(prevMessages => [
                ...prevMessages, 
                { role: 'assistant', content: 'Sorry, there was an error processing your request. Please try again.' }
            ]);
        } finally {
            setLoading(false);
            setInput('');
            // Only clear image if it was just uploaded (first message or new image)
            if (image) {
                setImage(null);
                setPreviewUrl(null);
                if (fileInputRef.current) {
                    fileInputRef.current.value = null;
                }
            }
        }
    };

    // Function to trigger file input click
    const triggerFileInput = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    // Function to clear selected image
    const clearImage = () => {
        setImage(null);
        setPreviewUrl(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = null;
        }
    };

    // Function to start new conversation
    const startNewConversation = () => {
        setMessages([]);
        setImage(null);
        setPreviewUrl(null);
        setConversationId(`chat-${Date.now()}-${Math.floor(Math.random() * 1000)}`);
        if (fileInputRef.current) {
            fileInputRef.current.value = null;
        }
    };

    return (
        <div className="chat-container">
            <div className="chat-header">
                <h2>AI Assistant</h2>
                {messages.length > 0 && (
                    <button onClick={startNewConversation} className="new-chat-btn">
                        New Chat
                    </button>
                )}
            </div>
            
            <div className="chat-messages">
                {messages && messages.length > 0 ? (
                    messages.map((msg, index) => (
                        <div key={index} className={`chat-message ${msg.role}`}>
                            <div className="message-content">
                                {msg.content}
                            </div>
                            {msg.image && (
                                <div className="image-container">
                                    <img
                                        src={msg.image}
                                        alt="Uploaded"
                                        className="chat-image"
                                        onError={(e) => {
                                            console.error("Image failed to load");
                                            e.target.onerror = null;
                                            e.target.src = 'fallback.png';
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                    ))
                ) : (
                    <div className="chat-placeholder">
                        <p>Start a conversation or upload an image...</p>
                        <p className="hint">Try asking about a part by uploading its image.</p>
                    </div>
                )}
                {loading && <div className="chat-message assistant loading">Typing...</div>}
                <div ref={messagesEndRef} /> {/* For auto-scrolling */}
            </div>
            
            {/* Image preview area */}
            {previewUrl && (
                <div className="image-preview">
                    <img src={previewUrl} alt="Preview" />
                    <button type="button" onClick={clearImage} className="clear-image">×</button>
                </div>
            )}
            
            <div className="chat-input">
                <input
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="Type a message..."
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey && !loading) {
                            e.preventDefault();
                            sendMessage();
                        }
                    }}
                />
                <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    style={{ display: 'none' }} // Hide the actual input
                    ref={fileInputRef}
                />
                <button type="button" onClick={triggerFileInput} className="image-button">
                    {image ? 'Change Image' : 'Add Image'}
                </button>
                <button onClick={sendMessage} disabled={loading} className="send-button">
                    {loading ? 'Sending...' : 'Send'}
                </button>
            </div>
        </div>
    );
};

export default Chat;